import {
  Button,
  Flex,
  Input,
  Select,
  Space,
  Switch,
  TableProps,
  Tooltip,
} from "antd";
import { ReactNode, useState } from "react";
import AddIcon from "../../../../assets/icons/AddIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import TitleBar from "../../../../components/common/TitleBar";
import CustomDrawer from "../../../../components/ui/CustomDrawer";
import CustomTable from "../../../../components/ui/CustomTable";
import Loader from "../../../../components/ui/Loader";
import {
  AllSalarySlabParams,
  SalarySlab,
} from "../../../../features/organization/contractual/SalarySlab/salarySlab.type";
import {
  useGetAllSalarySlabQuery,
  useGetSingleSalarySlabQuery,
  useUpdateSalarySlabMutation,
} from "../../../../features/organization/contractual/SalarySlab/salarySlabApi";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate } from "../../../../lib/time";
import { toast } from "../../../../lib/toast";
import {
  addCommaInNumber,
  countParPageSerialNumber,
} from "../../../../utils/number";
import { capitalize, valueOrNa } from "../../../../utils/string";
import { addKeyInArray, cn } from "../../../../utils/utils";
import MangeSlab from "./_components/MangeSlab";

const SalarySlabList = () => {
  const { open, handleOpen, handleClose } = useModal<SalarySlab>();
  const [updatingRow, setUpdatingRow] = useState<number | null>(null);
  const {
    page,
    limit,
    filter,
    keyword,
    handleFilter,
    handleLimit,
    handleSearch,
    handlePagination,
  } = useTable<AllSalarySlabParams>();

  // ------ Queries ------
  const { data, isLoading, isFetching } = useGetAllSalarySlabQuery({
    params: { ...filter, search: keyword },
  });
  const {
    data: slabDetails,
    isLoading: singleLoading,
    isFetching: singleFetching,
  } = useGetSingleSalarySlabQuery(open.data?.id as number, {
    skip: !open?.data?.id,
  });
  // ------ Mutations ------
  const [updateStatus] = useUpdateSalarySlabMutation();

  const handleStatusChange = async (record: SalarySlab, newStatus: string) => {
    setUpdatingRow(record.id);
    try {
      await updateStatus({
        id: record.id,
        data: { status: newStatus },
      }).unwrap();
      toast("success", "Slab status updated successfully.");
    } catch (err) {
      console.error(err);
      toast("error", "Status update failed");
    } finally {
      setUpdatingRow(null); // clear loading after finished
    }
  };

  // ------ Normalize Data for Details ------
  const {
    slab_name,
    fixed_amount,
    variable_amount,
    status,
    effective_date,
    created_at,
    updated_at,
  } = slabDetails?.data.salarySlab || {};

  const detailFields = [
    { label: "Slab Name", value: slab_name },
    { label: "Fixed Amount", value: addCommaInNumber(fixed_amount || 0) },
    { label: "Variable Amount", value: addCommaInNumber(variable_amount || 0) },
    {
      label: "Created On",
      value: formatDate(created_at, "DD MMMM YYYY, hh:mm A"),
    },
    {
      label: "Effective Date",
      value: formatDate(effective_date, "DD MMMM YYYY"),
    },
    {
      label: "Status",
      value: (
        <span
          className={cn(
            "status rounded-md",
            status === "active" ? "status-success" : "status-danger"
          )}
        >
          {capitalize(status || "")}
        </span>
      ),
    },
    {
      label: "Last Updated",
      value: formatDate(updated_at, "DD MMMM YYYY"),
    },
  ];

  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Slab Name",
      dataIndex: "slab_name",
      render: (val) => valueOrNa(val),
    },
    {
      align: "center",
      title: "Fixed Amount",
      dataIndex: "fixed_amount",
      render: (val) => `${addCommaInNumber(val || 0)} BDT`,
    },
    {
      align: "center",
      title: "Variable",
      dataIndex: "variable_amount",
      render: (val) => `${addCommaInNumber(val || 0)} BDT`,
    },
    {
      align: "center",
      title: "Created On",
      dataIndex: "created_at",
      render: (val) => formatDate(val, "DD MMMM YYYY, hh:mm A"),
    },
    {
      align: "center",
      title: "User In",
      dataIndex: "number_of_employee",
      render: (val) => `${addCommaInNumber(val || 0)} Employee`,
    },
    {
      align: "center",
      title: "Status",
      dataIndex: "status",
      render: (val, record) => (
        <Flex gap={6} align="center">
          <Switch
            loading={updatingRow === record.id} // only show loader for this row
            onChange={() =>
              handleStatusChange(
                record,
                val === "active" ? "inactive" : "active"
              )
            }
            checked={val === "active"}
            className="bg-active"
          />
          <span>{capitalize(val)}</span>
        </Flex>
      ),
    },
    {
      title: "Action",
      align: "center",
      width: 130,
      render: (record) => (
        <Space align="center">
          <Tooltip title="View">
            <Button
              onClick={() => handleOpen({ type: "VIEW", data: record })}
              className="action-view"
              size="small"
              icon={<ViewIcon />}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              onClick={() => handleOpen({ type: "UPDATE", data: record })}
              className="action-edit"
              size="small"
              icon={<EditIcon />}
              disabled={record?.number_of_employee !== 0}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <SidebarLayout title="Salary Slab">
      <TitleBar title="Salary Slab List">
        <Button
          onClick={() => handleOpen({ type: "ADD" })}
          color="primary"
          type="primary"
        >
          <AddIcon /> New Slab
        </Button>
      </TitleBar>
      <CustomTable
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.total}
        dataSource={addKeyInArray(data?.data.salarySlabs)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
              allowClear
            />
            <Select
              allowClear
              placeholder="Status"
              className="min-w-[90px]"
              rootClassName="icon-gray"
              popupMatchSelectWidth={120}
              suffixIcon={<SelectDownIcon />}
              onChange={(value) => handleFilter("status", value)}
              options={[
                { label: "Active", value: "active" },
                { label: "Inactive", value: "inactive" },
              ]}
            />
          </>
        }
      />

      <CustomDrawer
        width={460}
        title="Details"
        onClose={handleClose}
        open={open.open && open.type === "VIEW"}
      >
        {singleLoading || singleFetching ? (
          <Loader />
        ) : (
          detailFields.map((item) => (
            <DetailsItem
              key={item.label}
              label={item.label}
              value={item.value}
            />
          ))
        )}
      </CustomDrawer>

      <MangeSlab
        data={open.data}
        type={open.type}
        handleClose={handleClose}
        open={open.open && (open.type === "ADD" || open.type === "UPDATE")}
      />
    </SidebarLayout>
  );
};

const DetailsItem = ({
  label,
  value,
}: {
  label: string;
  value: string | number | ReactNode;
}) => {
  return (
    <Flex
      align="center"
      justify="space-between"
      className="border-b border-b-extra-light last:border-none py-4"
    >
      <span className="text-base font-normal text-body-text">{label}</span>
      <span className="text-base font-normal text-header">{value}</span>
    </Flex>
  );
};

export default SalarySlabList;
